// src/layouts/MainLayout.js
import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ChartBarIcon,
  HomeIcon,
  InboxIcon,
  UsersIcon,
  XMarkIcon,
  QueueListIcon,
  CreditCardIcon,
  MegaphoneIcon,
  FaceSmileIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FlagIcon,
  KeyIcon,
  TagIcon, // Icon for Subscriptions
  PhotoIcon, // Icon for Banners
  VideoCameraIcon, // Icon for Clips
  TvIcon, // Icon for Channels
  ArrowLeftOnRectangleIcon, // Icon for Withdrawals
  TicketIcon, // Icon for Ticketing
  LanguageIcon, // Icon for Languages
} from "@heroicons/react/24/outline";
import { useLocation, Link, Outlet } from "react-router-dom";
import logo from "./../assets/images/new-logo.svg";

import { useSelector } from "react-redux";
import { useTranslation } from "../contexts/LanguageContext";
import Notification from "./Notification";
import { Permissions } from "../components/Auth/permissions";
import TopBar from "../components/Common/TopBar";

const navigation = [
  { nameKey: "admin.nav.dashboard", href: "/dashboard", icon: HomeIcon, current: false, permission: Permissions.CAN_MANAGE_ANALYTIC },
  { nameKey: "admin.nav.users", href: "/users", icon: UsersIcon, current: false, permission: Permissions.CAN_MANAGE_USERS },
  { nameKey: "admin.nav.categories", href: "/categories", icon: QueueListIcon, current: false, permission: Permissions.CAN_MANAGE_CATEGORIES },
  { nameKey: "admin.nav.banners", href: "/banners", icon: PhotoIcon, current: false, permission: Permissions.CAN_MANAGE_CATEGORIES },
  { nameKey: "admin.nav.admins", href: "/admins-rights", icon: KeyIcon, current: false, permission: Permissions.CAN_MANAGE_USERS },
  { nameKey: "admin.nav.countries", href: "/countries", icon: FlagIcon, current: false, permission: Permissions.CAN_MANAGE_COUNTRIES },
  { nameKey: "admin.nav.languages", href: "/languages", icon: LanguageIcon, current: false, permission: Permissions.CAN_MANAGE_LANGUAGES },
  { nameKey: "admin.nav.subscriptions", href: "/subscriptions", icon: TagIcon, current: false, permission: Permissions.CAN_MANAGE_SUBSCRIPTIONS },
  { nameKey: "admin.nav.auctions", href: "/auctions", icon: InboxIcon, current: false, permission: Permissions.CAN_MANAGE_PACKAGES },
  { nameKey: "admin.nav.clips", href: "/clips", icon: VideoCameraIcon, current: false, permission: Permissions.CAN_MANAGE_MODULES },
  { nameKey: "admin.nav.channels", href: "/channels", icon: TvIcon, current: false, permission: Permissions.CAN_MANAGE_MODULES },
  { nameKey: "admin.nav.withdrawals", href: "/withdrawals", icon: ArrowLeftOnRectangleIcon, current: false, permission: Permissions.CAN_MANAGE_PAYMENTS },
  {
    nameKey: "admin.nav.marketing",
    href: "#",
    icon: MegaphoneIcon,
    current: false,
    permission: Permissions.CAN_MANAGE_MARKETING,
    children: [
      { nameKey: "admin.nav.notifications", href: "/marketing/notifications", current: false },
      { nameKey: "admin.nav.coupons", href: "/marketing/coupons", current: false },
    ],
  },
  { nameKey: "admin.nav.orders", href: "/orders", icon: BanknotesIcon, current: false, permission: Permissions.CAN_MANAGE_PAYMENTS },
  { nameKey: "admin.nav.transactions", href: "/transactions", icon: CreditCardIcon, current: false, permission: Permissions.CAN_MANAGE_ORDERS },
  { nameKey: "admin.nav.ticketing", href: "/ticketing", icon: TicketIcon, current: false, permission: Permissions.CAN_MANAGE_TICKETS },
  { nameKey: "admin.nav.feedbacks", href: "/feedbacks", icon: FaceSmileIcon, current: false, permission: Permissions.CAN_MANAGE_FEEDBACKS },
  { nameKey: "admin.nav.reportedAccounts", href: "/report-accounts", icon: ChartBarIcon, current: false, permission: Permissions.CAN_MANAGE_FEEDBACKS },
  { nameKey: "admin.nav.reports", href: "/reports", icon: InboxIcon, current: false, permission: Permissions.CAN_MANAGE_REPORTS },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function MainLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // To track open submenu
  const location = useLocation();
  const { admin } = useSelector((state) => state.auth);
  const { t } = useTranslation();

  const toggleMenu = (menuName) => {
    setOpenMenu(openMenu === menuName ? null : menuName);
  };

  return (
    <>
      <div>
        {/* Mobile Sidebar */}
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative z-40 md:hidden" onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-dashboardDark" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-dashboardDark pt-5 pb-4">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute top-0 right-0 -mr-12 pt-2">
                      <button
                        type="button"
                        className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex flex-shrink-0 items-center px-4">
                    <img className="h-8 w-auto" src={logo} alt="Your Company" />
                  </div>
                  <div className="mt-5 h-0 flex-1 overflow-y-auto">
                    <nav className="space-y-1 px-2">
                      {navigation.map((item) => {
                        if (item.permission && !admin?.permissions?.[item.permission]) return null;
                        return (
                          <div key={item.nameKey}>
                            {item.children && item.children.length > 0 ? (
                              <>
                                <button
                                  onClick={() => toggleMenu(item.nameKey)}
                                  className={classNames(
                                    location.pathname === item.href
                                      ? "bg-dashboardCoral text-white"
                                      : "text-themeLightGray hover:bg-gray-700 hover:text-white",
                                    "group flex items-center w-full px-2 py-2 text-base font-medium rounded-md"
                                  )}
                                >
                                  <div className="flex justify-between">
                                    <item.icon
                                      className={classNames(
                                        location.pathname === item.href
                                          ? "text-gray-300"
                                          : "text-gray-400 group-hover:text-themeLightGray",
                                        "me-3 flex-shrink-0 h-6 w-6"
                                      )}
                                      aria-hidden="true"
                                    />
                                    {t(item.nameKey)}
                                  </div>
                                  <div>
                                    {openMenu === item.nameKey ? (
                                      <ChevronUpIcon className="text-gray-400 w-4" />
                                    ) : (
                                      <ChevronDownIcon className="text-gray-400 w-4" />
                                    )}
                                  </div>
                                </button>
                                {openMenu === item.nameKey && (
                                  <div className="ms-8 space-y-1">
                                    {item.children.map((subItem) => {
                                      if (subItem.permission && !admin?.permissions?.[subItem.permission]) return null;
                                      return (
                                        <Link
                                          key={subItem.nameKey}
                                          to={subItem.href}
                                          className={classNames(
                                            location.pathname === subItem.href
                                              ? "bg-dashboardCoral text-white"
                                              : "text-themeLightGray hover:bg-gray-700 hover:text-white",
                                            "group flex items-center px-2 py-2 text-base font-medium rounded-md"
                                          )}
                                        >
                                          {t(subItem.nameKey)}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                )}
                              </>
                            ) : (
                              <Link
                                to={item.href}
                                className={classNames(
                                  location.pathname === item.href
                                    ? "bg-dashboardCoral text-white"
                                    : "text-themeLightGray hover:bg-gray-700 hover:text-white",
                                  "group flex items-center px-2 py-2 text-base font-medium rounded-md"
                                )}
                              >
                                <item.icon
                                  className={classNames(
                                    location.pathname === item.href
                                      ? "text-white"
                                      : "text-themeLightGray group-hover:text-gray-300",
                                    "me-3 flex-shrink-0 h-6 w-6"
                                  )}
                                  aria-hidden="true"
                                />
                                {t(item.nameKey)}
                              </Link>
                            )}
                          </div>
                        );
                      })}
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
              <div className="w-14 flex-shrink-0" aria-hidden="true"></div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Desktop Sidebar — anchored to inline-start so it sits on the right in RTL */}
        <div className="hidden md:fixed md:inset-y-0 md:start-0 md:flex md:w-64 md:flex-col">
          <div className="flex min-h-0 flex-1 flex-col bg-dashboardDark">
            <div className="flex py-4 flex-shrink-0 items-center bg-dashboardDark px-4">
              <img className="w-auto" src={logo} alt="Your Company" />
            </div>
            <div className="flex flex-1 flex-col overflow-y-auto">
              <nav className="flex-1 space-y-1 px-2 py-4">
                {navigation.map((item) => {
                  if (item.permission && !admin?.permissions?.[item.permission]) return null;
                  return (
                    <div key={item.nameKey}>
                      {item.children && item.children.length > 0 ? (
                        <>
                          <button
                            onClick={() => toggleMenu(item.nameKey)}
                            className={classNames(
                              location.pathname === item.href
                                ? "bg-dashboardCoral text-white"
                                : "text-themeLightGray font-normal hover:bg-gray-700 hover:text-white",
                              "group flex w-full justify-between items-center px-2 py-2 text-sm font-medium rounded-md"
                            )}
                          >
                            <div className="flex justify-between">
                              <item.icon
                                className={classNames(
                                  location.pathname === item.href
                                    ? "text-gray-300"
                                    : "text-gray-400 group-hover:text-themeLightGray",
                                  "me-3 flex-shrink-0 h-6 w-6"
                                )}
                                aria-hidden="true"
                              />
                              {t(item.nameKey)}
                            </div>
                            <div>
                              {openMenu === item.nameKey ? (
                                <ChevronUpIcon className="text-gray-400 w-4" />
                              ) : (
                                <ChevronDownIcon className="text-gray-400 w-4" />
                              )}
                            </div>
                          </button>
                          {openMenu === item.nameKey && (
                            <div className="ms-8 space-y-1">
                              {item.children.map((subItem) => {
                                if (subItem.permission && !admin?.permissions?.[subItem.permission]) return null;
                                return (
                                  <Link
                                    key={subItem.nameKey}
                                    to={subItem.href}
                                    className={classNames(
                                      location.pathname === subItem.href
                                        ? "bg-dashboardCoral text-white"
                                        : "text-themeLightGray font-normal hover:bg-gray-700 hover:text-white",
                                      "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                                    )}
                                  >
                                    {t(subItem.nameKey)}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <Link
                          to={item.href}
                          className={classNames(
                            location.pathname === item.href
                              ? "bg-dashboardCoral text-white"
                              : "text-themeLightGray font-normal hover:bg-gray-700 hover:text-white",
                            "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                          )}
                        >
                          <item.icon
                            className={classNames(
                              location.pathname === item.href
                                ? "text-white"
                                : "text-gray-400 group-hover:text-themeLightGray",
                              "me-3 flex-shrink-0 h-6 w-6"
                            )}
                            aria-hidden="true"
                          />
                          {t(item.nameKey)}
                        </Link>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
        {/* Main content area — padding-inline-start flips to the correct side in RTL */}
        <div className="flex flex-col md:ps-64">
          {/* New TopBar */}
          <TopBar onMobileMenuClick={() => setSidebarOpen(true)} />
          
          <main className="flex-1 bg-dashboardBg min-h-screen">
            <div className="py-4">
              <div className="px-4 sm:px-6 md:px-6">
                {children}
                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>
      <Notification />
    </>
  );
}
